import React from 'react';
import { Card, Col, Button, Form, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck, faEnvelope, faMapMarkerAlt, faBell, faChartBar, faEdit, faStar } from '@fortawesome/free-solid-svg-icons';

const RewardCard = ({ reward, index, editingIndex, editedReward, handleEdit, handleInputChange, handleSave, setEditingIndex, demographicsOptions, role, current }) => {
    const getMultiplier = (tier) => {
        switch (tier.toLowerCase()) {
            case 'bronze':
                return 1;
            case 'silver':
                return 2;
            case 'gold':
                return 3;
            case 'platinum':
                return 4;
            default:
                return 0; // Default multiplier if tier is unknown
        }
    };
    return (
        <Col sm={12} md={6} lg={4} xl={3} className="mb-4">
            <Card className={`card w-100 shadow-lg ${editingIndex === index && role === 'admin' ? 'border-success-color border-2' : ''} ${current ? 'reward-tier' : ''}`}>
                <Card.Header className="bg-success-color text-white card-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            {reward.tier} {current ? <span className="badge text-bg-info">Current</span> : ''}
                        </h5>
                        <Badge bg="light" text="dark" className="tier-badge">
                            Tier {reward.tier} <span className="ms-2">x{reward.multiplier}</span>
                        </Badge>
                    </div>
                </Card.Header>

                <Card.Body>
                    {editingIndex === index && role === 'admin' ? (
                        <div className="edit-form">
                            <Form.Group className="mb-3">
                                <Form.Label>Tier</Form.Label>
                                <Form.Control disabled type="text" value={editedReward.tier} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check type="checkbox" label="Over 18 y.o." checked={editedReward.age} onChange={(e) => handleInputChange(e, 'age')} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check type="checkbox" label="Email Required" checked={editedReward.email} onChange={(e) => handleInputChange(e, 'email')} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Location Tracking</Form.Label>
                                <div className="ms-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="ON (Foreground/Background)"
                                        checked={editedReward.foregroundLocation}
                                        onChange={(e) => handleInputChange(e, 'foregroundLocation')}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="ON (All the time)"
                                        checked={editedReward.allTimeLocation}
                                        onChange={(e) => handleInputChange(e, 'allTimeLocation')}
                                    />
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Enable Notifications"
                                    checked={editedReward.notifications}
                                    onChange={(e) => handleInputChange(e, 'notifications')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Multiplier</Form.Label>
                                <Form.Control type="number" value={editedReward.multiplier} onChange={(e) => handleInputChange(e, 'multiplier')} min="1" max="4" />
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Demographics Data</Form.Label>
                                <Form.Select value={editedReward.demographics} onChange={(e) => handleInputChange(e, 'demographics')}>
                                    {demographicsOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <div className="d-flex justify-content-between gap-2">
                                <Button variant="success" className="w-50" onClick={handleSave}>
                                    Save Changes
                                </Button>
                                <Button variant="outline-secondary" className="w-50" onClick={() => setEditingIndex(null)}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faUserCheck} className="me-3 text-success" />
                                <div>
                                    <strong>Age Requirement:</strong>
                                    <br />
                                    {reward.age ? 'Over 18 years old' : 'No age restriction'}
                                </div>
                            </ListGroup.Item>

                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faEnvelope} className="me-3 text-primary" />
                                <div>
                                    <strong>Email Verification:</strong>
                                    <br />
                                    {reward.email ? 'Required' : 'Not required'}
                                </div>
                            </ListGroup.Item>

                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-3 text-danger" />
                                <div>
                                    <strong>Location Tracking:</strong>
                                    <br />
                                    {reward.foregroundLocation && 'Foreground/Background'}
                                    {reward.allTimeLocation && '/All the time'}
                                </div>
                            </ListGroup.Item>

                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faBell} className="me-3 text-warning" />
                                <div>
                                    <strong>Notifications:</strong>
                                    <br />
                                    {reward.notifications ? 'Enabled' : 'Disabled'}
                                </div>
                            </ListGroup.Item>

                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faStar} className="me-3 text-info" />
                                <div>
                                    <strong>Multiplier:</strong>
                                    <br />
                                    {reward.multiplier ? `x${reward.multiplier}` : 'Not set'}
                                </div>
                            </ListGroup.Item>

                            <ListGroup.Item className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faChartBar} className="me-3 text-info" />
                                <div>
                                    <strong>Data Sharing:</strong>
                                    <br />
                                    {reward.demographics}
                                </div>
                            </ListGroup.Item>
                        </ListGroup>
                    )}

                    {editingIndex === null && role === 'admin' && (
                        <div className="mt-4 text-center">
                            <Button
                                variant={editingIndex === index && role === 'admin' ? 'primary' : 'outline-success'}
                                className="w-100"
                                onClick={() => handleEdit(index)}
                            >
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                {editingIndex === index && role === 'admin' ? 'Editing...' : 'Edit Settings'}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );
};

export default RewardCard;
